import React, { useEffect, useMemo, useRef } from 'react'
import { Upload } from 'react-bootstrap-icons'

/**
 * The uploader that was duplicated verbatim in AddBannerModal and
 * AddOfferModal. Media goes first in a form, so this is normally the first
 * thing in the body.
 *
 * `previewUrl` is the already-saved image when editing; `file` is the pending
 * selection. A pending file always wins over the saved one.
 */
const ImageDropzone = ({
  id = 'image-upload',
  label,
  required = false,
  file,
  previewUrl,
  onChange,
  onRemove,
  accept = 'image/*',
  hint = 'PNG or JPG, up to 5 MB',
  height,
  error,
}) => {
  const inputRef = useRef(null)

  // One object URL per selected file, revoked when it is replaced or the modal
  // unmounts — creating it inline would leak a URL on every render.
  const localPreview = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  )
  useEffect(
    () => () => {
      if (localPreview) URL.revokeObjectURL(localPreview)
    },
    [localPreview],
  )

  const preview = localPreview || previewUrl || null

  const open = () => inputRef.current && inputRef.current.click()

  const handleChange = (event) => {
    const selected = event.target.files && event.target.files[0]
    onChange(selected || null)
  }

  const handleRemove = (event) => {
    event.stopPropagation()
    if (inputRef.current) inputRef.current.value = ''
    onChange(null)
    if (onRemove) onRemove()
  }

  return (
    <div className={`app-modal__field ${error ? 'app-modal__field--invalid' : ''}`.trim()}>
      {label && (
        <span className='app-modal__label'>
          {label}
          {required && <span className='app-modal__required'>*</span>}
        </span>
      )}

      <div
        className={`app-modal__drop ${preview ? 'app-modal__drop--filled' : ''}`.trim()}
        style={height ? { '--am-drop-height': height } : undefined}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            open()
          }
        }}
        role='button'
        tabIndex={0}
      >
        {preview ? (
          <>
            <img className='app-modal__drop-img' src={preview} alt='' />
            <div className='app-modal__drop-bar'>
              <span className='app-modal__drop-name'>
                {file ? file.name : 'Current image'}
              </span>
              <span className='d-flex gap-2 flex-shrink-0'>
                <button type='button' className='btn btn-sm btn-light' onClick={open}>
                  Replace
                </button>
                {file && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger'
                    onClick={handleRemove}
                  >
                    Remove
                  </button>
                )}
              </span>
            </div>
          </>
        ) : (
          <>
            <Upload className='app-modal__drop-icon' />
            <span className='app-modal__drop-title'>Click to upload an image</span>
            <span className='app-modal__drop-hint'>{hint}</span>
          </>
        )}
      </div>

      {/* Deliberately not `required`: a hidden required input makes the browser
          abort submit with "not focusable" and no visible message. Callers
          check for a missing image in onSubmit and surface it in the footer. */}
      <input
        ref={inputRef}
        id={id}
        className='d-none'
        type='file'
        accept={accept}
        onChange={handleChange}
      />

      {error && <span className='app-modal__help app-modal__help--error'>{error}</span>}
    </div>
  )
}

export default ImageDropzone
